import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { devLog, devError } from '../utils/devLogger';
import { TOSS_PAYMENTS_CLIENT_KEY } from '../services/tossPayments';
import { isShoppingMode } from '../config/appMode';
import { createPayment, approvePayment } from '../lib/api/payment/paymentApi';
import PrimaryButton from './PrimaryButton';
import { toast } from 'react-toastify';

interface TossPaymentWidgetProps {
  amount: number;
  orderId: string;
  orderName: string;
  customerName?: string;
  customerEmail?: string;
  onSuccess?: (paymentData: any) => void;
  onFail?: (error: any) => void;
  disabled?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PaymentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
`;

const AmountDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const AmountLabel = styled.span`
  font-size: 16px;
  color: #666;
  font-weight: 500;
`;

const AmountValue = styled.span`
  font-size: 20px;
  color: #000;
  font-weight: 700;
`;

const PaymentMethodSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
`;

const PaymentMethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
`;

const PaymentMethodButton = styled.button<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  border: 2px solid ${props => props.selected ? '#7a6647' : '#e5e5e5'};
  border-radius: 8px;
  background: ${props => props.selected ? '#f9f8f7' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #7a6647;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaymentMethodIcon = styled.div`
  font-size: 24px;
  color: #7a6647;
`;

const PaymentMethodName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const PaymentButton = styled(PrimaryButton)`
  margin-top: 20px;
  font-size: 18px;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  
  &::after {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #7a6647;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

type PaymentMethod = 'CARD' | 'TRANSFER';

const PAYMENT_METHODS = [
  { type: 'CARD' as PaymentMethod, name: '카드결제', icon: '💳' },
  { type: 'TRANSFER' as PaymentMethod, name: '계좌이체', icon: '🏦' }
];

export const TossPaymentWidget: React.FC<TossPaymentWidgetProps> = ({
  amount,
  orderId,
  orderName,
  customerName = '구매자',
  customerEmail = 'customer@example.com',
  onSuccess,
  onFail,
  disabled = false
}) => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('CARD');
  const [loading, setLoading] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  // 토스 페이먼츠 SDK 로드
  useEffect(() => {
    const loadTossPayments = async () => {
      try {
        if ((window as any).TossPayments) {
          setSdkLoaded(true);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://js.tosspayments.com/v1/payment';
        script.async = true;
        
        // 웹뷰 환경에서의 안정성을 위한 타임아웃 추가
        const timeoutId = setTimeout(() => {
          devError('토스 페이먼츠 SDK 로드 타임아웃');
          toast.error('결제 시스템 로드가 지연되고 있습니다. 네트워크를 확인해주세요.');
        }, 10000); // 10초 타임아웃
        
        script.onload = () => {
          clearTimeout(timeoutId);
          devLog('토스 페이먼츠 SDK 로드 완료');
          setSdkLoaded(true);
        };
        
        script.onerror = (error) => {
          clearTimeout(timeoutId);
          devError('토스 페이먼츠 SDK 로드 실패:', error);
          toast.error('결제 시스템을 불러올 수 없습니다. 네트워크를 확인해주세요.');
        };
        
        document.head.appendChild(script);
      } catch (error) {
        devError('SDK 로드 중 오류:', error);
      }
    };

    loadTossPayments();
  }, []);

  const handlePayment = async () => {
    // 쇼핑 모드에서만 결제 허용
    if (!isShoppingMode()) {
      toast.error('현재 모드에서는 결제 기능을 사용할 수 없습니다.');
      return;
    }

    if (!sdkLoaded) {
      toast.error('결제 시스템이 준비되지 않았습니다.');
      return;
    }

    // 웹뷰 환경 감지
    const isWebView = () => {
      const userAgent = navigator.userAgent;
      return (
        userAgent.includes('wv') || // Android WebView
        (userAgent.includes('Version') && userAgent.includes('Mobile')) || // iOS WebView
        (window as any).flutter_inappwebview !== undefined || // Flutter WebView
        (window as any).FlutterBridge !== undefined // Custom Flutter Bridge
      );
    };

    devLog('🌐 환경 감지:', {
      isWebView: isWebView(),
      userAgent: navigator.userAgent,
      hasFlutterBridge: !!(window as any).FlutterBridge,
      origin: window.location.origin
    });

    setLoading(true);
    
    try {
      devLog('결제 프로세스 시작:', {
        amount,
        orderId,
        orderName,
        selectedMethod,
        customerName,
        customerEmail
      });

      // 1단계: 서버에 결제 요청
      devLog('1단계: 결제 요청 API 호출');
      const paymentRequest = await createPayment({
        orderId: orderId,
        amount: amount
      });
      
      devLog('결제 요청 성공:', paymentRequest);

      // 2단계: 토스 페이먼츠 인스턴스 생성 및 결제창 호출
      devLog('2단계: 토스 결제창 호출');
      const tossPayments = (window as any).TossPayments(TOSS_PAYMENTS_CLIENT_KEY);
      
      // 웹뷰와 웹 환경을 고려한 Base URL 설정
      const getBaseUrl = () => {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return window.location.origin;
        }
        return window.location.origin;
      };
      
      const baseUrl = getBaseUrl();
      
      // Promise 방식으로 결제 진행
      const paymentResult = await new Promise((resolve, reject) => {
        const paymentMethod = {
          'CARD': '카드',
          'TRANSFER': '계좌이체'
        }[selectedMethod];
        
        tossPayments.requestPayment(paymentMethod, {
          amount: amount,
          orderId: orderId,
          orderName: orderName,
          customerName: customerName,
          customerEmail: customerEmail,
          successUrl: `${baseUrl}/payment/success?orderId=${orderId}&amount=${amount}&orderName=${encodeURIComponent(orderName)}`,
          failUrl: `${baseUrl}/payment/fail?orderId=${orderId}&amount=${amount}`
        })
        .then((result: any) => {
          devLog('토스 결제 완료:', result);
          resolve(result);
        })
        .catch((error: any) => {
          devError('토스 결제 실패:', error);
          reject(error);
        });
      });
      
      // 3단계: 결제 승인 (paymentKey가 있는 경우)
      if ((paymentResult as any).paymentKey) {
        devLog('3단계: 결제 승인 API 호출');
        const approvalResult = await approvePayment({
          paymentKey: (paymentResult as any).paymentKey,
          amount: amount,
          orderId: orderId
        });
        
        devLog('결제 승인 성공:', approvalResult);
        
        // 성공 시 콜백 호출
        if (onSuccess) {
          onSuccess({
            paymentKey: approvalResult.paymentKey,
            orderId: approvalResult.orderId,
            amount: approvalResult.amount,
            approvedAt: approvalResult.approvedAt,
            paymentMethod: selectedMethod,
            customerName,
            customerEmail
          });
        }
      } else {
        // 리다이렉트 방식의 경우 (paymentKey가 URL 파라미터로 전달됨)
        devLog('리다이렉트 방식 결제 완료');
        if (onSuccess) {
          onSuccess({
            orderId,
            amount,
            paymentMethod: selectedMethod,
            customerName,
            customerEmail
          });
        }
      }
      
    } catch (error: any) {
      devError('결제 실패:', error);
      
      // 사용자가 결제를 취소한 경우
      if (error.code === 'USER_CANCEL') {
        toast.info('결제가 취소되었습니다.');
      } else {
        toast.error(error.message || '결제 중 오류가 발생했습니다.');
      }
      
      // 실패 시 콜백 호출
      if (onFail) {
        onFail(error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!sdkLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <PaymentSection>
        <AmountDisplay>
          <AmountLabel>결제 금액</AmountLabel>
          <AmountValue>{amount.toLocaleString()}원</AmountValue>
        </AmountDisplay>

        <PaymentMethodSection>
          <SectionTitle>결제 수단</SectionTitle>
          <PaymentMethodGrid>
            {PAYMENT_METHODS.map((method) => (
              <PaymentMethodButton
                key={method.type}
                selected={selectedMethod === method.type}
                onClick={() => setSelectedMethod(method.type)}
                disabled={loading || disabled}
              >
                <PaymentMethodIcon>{method.icon}</PaymentMethodIcon>
                <PaymentMethodName>{method.name}</PaymentMethodName>
              </PaymentMethodButton>
            ))}
          </PaymentMethodGrid>
        </PaymentMethodSection>

        <PaymentButton
          onClick={handlePayment}
          disabled={loading || disabled}
        
        >
          {loading ? '결제 진행 중...' : `${amount.toLocaleString()}원 결제하기`}
        </PaymentButton>
      </PaymentSection>
    </Container>
  );
};

export default TossPaymentWidget;